import React from 'react';
import Axios from 'axios';
import { useMutation } from '@tanstack/react-query';
import { plato_api_glossary_term_path } from '@/modules/routes';
import { queryClient } from '@/utils/ReactQuery';
import QueryKeys from '@/utils/QueryKeys';
import { TypeToConfirmModal, useModalManager } from '@/components/common/Modal';

interface DeleteGlossaryTermButtonProps {
  glossaryTerm: {
    id: number;
    section_id: number;
    term: string;
  }
  onSuccess: () => void;
}

const DeleteGlossaryTermButton = ({ glossaryTerm, onSuccess }: DeleteGlossaryTermButtonProps) => {
  const modalManager = useModalManager();

  const { mutate: deleteGlossaryTerm, isLoading, isError, isSuccess } = useMutation({
    mutationFn: () => Axios.delete(plato_api_glossary_term_path(glossaryTerm.id)),
    mutationKey: [QueryKeys.GlossaryTerms, QueryKeys.Mutations.Delete, glossaryTerm.id],
    onError: (err) => {
      // eslint-disable-next-line no-console
      console.error(err);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries([QueryKeys.GlossaryTerms, glossaryTerm.section_id]);
      modalManager.close();
    },
    onSuccess: () => {
      if (onSuccess) onSuccess();
      modalManager.close();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    deleteGlossaryTerm();
  };

  const confirmationPromptText = `Deleting the glossary term
    <strong>${glossaryTerm.term}</strong> will affect all locations where it is used.`;

  return (
    <>
      <button
        className="action tw-ml-1"
        disabled={isLoading || isSuccess}
        type="button"
        onClick={modalManager.open}
      >
        Delete
      </button>

      <TypeToConfirmModal
        close={modalManager.close}
        confirmationPrompt={<span dangerouslySetInnerHTML={{ __html: confirmationPromptText }} />}
        error={isError}
        headerText="Delete Glossary Term"
        isOpen={modalManager.isOpen}
        submitting={isLoading || isSuccess}
        warningMessage="Deleted terms cannot be recovered."
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default DeleteGlossaryTermButton;
